import { brandSwatch, brandSwatchClasses } from "@/utils/posStores";

interface BrandTagProps {
  brand: string;
  /** Text shown in the tag, colored by the brand's swatch. Defaults to the
   *  brand name itself (used for the brand-group headings), but callers
   *  pairing a brand with a store should pass the LOCATION text here instead
   *  — staff read the brand from the color, the location from the text. */
  label?: string;
  size?: "xs" | "sm";
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<BrandTagProps["size"]>, string> = {
  xs: "text-xs px-2 py-0.5 gap-1",
  sm: "text-sm px-2.5 py-1 gap-1.5",
};

const DOT_SIZE: Record<NonNullable<BrandTagProps["size"]>, string> = {
  xs: "w-1.5 h-1.5",
  sm: "w-2 h-2",
};

/**
 * A brand's colour, paired with either the brand name (default) or a store's
 * location text (`label`). When `label` is given the brand name is dropped
 * from the visible text — staff read the brand from the swatch colour and
 * the location from the text — but it survives as the `title` and as
 * visually-hidden text for accessibility (DESIGN.md > Do's and Don'ts: never rely on colour
 * alone). Used everywhere a store's brand appears in orderSpy so the same
 * brand always reads as the same swatch.
 */
export function BrandTag({ brand, label, size = "sm", className = "" }: BrandTagProps) {
  if (!brand) return null;
  const classes = brandSwatchClasses(brandSwatch(brand));
  const text = label ?? brand;
  return (
    <span
      title={brand}
      className={`inline-flex items-center rounded-full font-semibold min-w-0 ${classes.bg} ${classes.text} ${SIZE_CLASSES[size]} ${className}`}
    >
      <span aria-hidden="true" className={`flex-none rounded-full ${classes.dot} ${DOT_SIZE[size]}`} />
      {label ? <span className="sr-only">{brand}: </span> : null}
      <span className="truncate">{text}</span>
    </span>
  );
}

export default BrandTag;
